<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {useDeclarationCotisationStore} from "@/stores/Impression/DeclarationCotisation.js";
import useTools from "@/use/Tools.js";
import axios from "axios";

export default {
    
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
        type: String
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const declarationCotisation = ref({
            staticCotisation: false,
            type: props.type,
        });
        const declarationCotisationStore = useDeclarationCotisationStore();
        
        function findById(id) {
            loading.value = true;
            axios.get(`/contributionDeclarations/${id}`).then((response) => {
                declarationCotisation.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/contributionDeclarations', declarationCotisation.value).then((response) => {
                    declarationCotisationStore.addDeclarationCotisation(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/contributionDeclarations', declarationCotisation.value).then((response) => {
                    declarationCotisationStore.updateDeclarationCotisation(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/contributionDeclarations/${declarationCotisation.value.id}`).then(() => {
                    declarationCotisationStore.deleteDeclarationCotisation(declarationCotisation.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });
        
        return {
            declarationCotisationStore,
            mxRules,
            dialog,
            loading,
            overlay,
            declarationCotisation,
            readonly,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="600"
        persistent
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <v-row dense>
                            <v-col cols="12">
                                <w-label :label="$t('common.code')" required/>
                                <w-text-field
                                    v-model="declarationCotisation.code"
                                    :readonly="readonly"
                                />
                            </v-col>
                            <v-col cols="12">
                                <w-label :label="$t('contribution_declaration.nature')" required/>
                                <w-text-field
                                    v-model="declarationCotisation.nature"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-checkbox
                                    v-model="declarationCotisation.staticCotisation"
                                    :true-value="false"
                                    :false-value="true"
                                    :label="$t('contribution_declaration.contribution_by_rate')"
                                    hide-details
                                    class="mt-n2"
                                />
                            </v-col>
                            <v-col cols="12" v-if="!declarationCotisation.staticCotisation">
                                <w-label :label="$t('contribution_declaration.plate')" required/>
                                <text-field-pay-section
                                    v-model="declarationCotisation.assiete"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12" v-if="!declarationCotisation.staticCotisation">
                                <w-label :label="$t('contribution_declaration.rate')" required/>
                                <w-text-field
                                    v-model="declarationCotisation.taux"
                                    type="number"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12" v-if="declarationCotisation.staticCotisation">
                                <w-label :label="$t('contribution_declaration.contribution')" required/>
                                <text-field-pay-section
                                    v-model="declarationCotisation.cotisation"
                                    :readonly="readonly"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>